/** Roles where Space activates/toggles the control instead of scrolling/panning. */
const SPACE_ACTIVATABLE_ROLES = new Set([
  "button",
  "checkbox",
  "combobox",
  "link",
  "listbox",
  "menuitem",
  "menuitemcheckbox",
  "menuitemradio",
  "option",
  "radio",
  "slider",
  "spinbutton",
  "switch",
  "tab",
  "textbox",
]);

/** Text inputs / selects where shortcuts should defer to the native control. */
export function isEditableKeyboardTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  return (
    target.isContentEditable ||
    target.tagName === "INPUT" ||
    target.tagName === "SELECT" ||
    target.tagName === "TEXTAREA"
  );
}

/**
 * True when Space should not claim temporary pan — text editing or activating a
 * focused control (toolbar buttons, Radix Select triggers, links, etc.).
 */
export function shouldIgnoreSpacePanTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  if (
    isEditableKeyboardTarget(target) ||
    target.tagName === "BUTTON" ||
    target.tagName === "A" ||
    target.tagName === "SUMMARY"
  ) {
    return true;
  }

  const role = target.getAttribute("role");
  if (role && SPACE_ACTIVATABLE_ROLES.has(role)) {
    return true;
  }

  // Focus can land on a child of a control (e.g. icon inside a button).
  return Boolean(
    target.closest(
      "button, a[href], summary, [role='button'], [role='checkbox'], [role='combobox'], [role='link'], [role='listbox'], [role='menuitem'], [role='menuitemcheckbox'], [role='menuitemradio'], [role='option'], [role='radio'], [role='slider'], [role='spinbutton'], [role='switch'], [role='tab'], [role='textbox']"
    )
  );
}

export function isSpaceKey(event: KeyboardEvent) {
  return event.key === " " || event.code === "Space";
}

/**
 * Space-pan should only engage when the pointer is over the viewport container
 * or keyboard focus is already inside it — otherwise Space must keep scrolling
 * the surrounding page (sidebars, lists, etc.).
 */
export function isSpacePanContextActive(
  container: HTMLElement | null,
  pointerOver: boolean
) {
  if (!container) {
    return false;
  }
  if (pointerOver) {
    return true;
  }
  const active = document.activeElement;
  return active instanceof Node && container.contains(active);
}
